import { ShieldCheck, Database, Users } from "lucide-react";

import { ENV } from "@/env";
import { AdminLayout } from "@/components/admin/layout/AdminLayout";
import SetupCard from "./SetupCard";
import StatusBadge from "./StatusBadge";
import { useAuthGuardAdmin } from "./hooks/useAuthGuardAdmin";

export function getProjectRefFromUrl(supabaseUrl: string): string {
  const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/);
  return match?.[1] ?? "";
}

const seedAdminSql = `insert into public.users (id, email, role)
select id, email, 'super_admin'
from auth.users
where email in ('SEU_EMAIL@DOMINIO')
on conflict (id) do update set role = excluded.role;

insert into public.app_roles (user_id, email, role)
select id, email, 'admin'
from auth.users
where email in ('SEU_EMAIL@DOMINIO')
on conflict (email) do update set role = excluded.role, user_id = excluded.user_id;

insert into public.user_roles (user_id, role)
select id, 'super_admin'
from auth.users
where email in ('SEU_EMAIL@DOMINIO')
on conflict (user_id, role) do nothing;`;

const suggestedRedirects = ["http://localhost:5173/*", "{{SITE_URL}}/*"];

function AdminSetupPage() {
  const { isAdmin, userEmail, loading } = useAuthGuardAdmin();

  const supabaseUrl = ENV.SUPABASE_URL ?? "";
  const supabaseAnon = ENV.SUPABASE_ANON ?? "";
  const envReady = Boolean(supabaseUrl && supabaseAnon);
  const projectRef = supabaseUrl ? getProjectRefFromUrl(supabaseUrl) : "";

  const providersUrl = projectRef
    ? `https://supabase.com/dashboard/project/${projectRef}/auth/providers`
    : "https://supabase.com/dashboard";
  const sqlUrl = projectRef
    ? `https://supabase.com/dashboard/project/${projectRef}/sql/new`
    : "https://supabase.com/dashboard";
  const usersUrl = projectRef
    ? `https://supabase.com/dashboard/project/${projectRef}/auth/users`
    : "https://supabase.com/dashboard";

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-600">Verificando permissões...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <AdminLayout
      tone="light"
      title="Setup Rápido do Admin"
      description="Configure autenticação, roles e acessos em poucos passos para liberar toda a experiência Toc Toc."
      breadcrumbs={[
        { label: "Admin", href: "/admin" },
        { label: "Infraestrutura" },
        { label: "Setup" },
      ]}
      status={{ tone: envReady ? "success" : "warning", label: envReady ? "Variáveis prontas" : "Ajuste variáveis do Supabase" }}
    >
      {!envReady && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 shadow-sm">
          <strong className="font-semibold">Variáveis ausentes.</strong> Adicione <code className="font-mono">VITE_SUPABASE_URL</code> e <code className="font-mono">VITE_SUPABASE_ANON_KEY</code> nas configurações de ambiente do Lovable e publique novamente o projeto.
        </div>
      )}

      <section className="rounded-2xl border border-slate-200/60 bg-white/80 p-6 shadow-sm backdrop-blur">
        <h2 className="text-lg font-semibold text-slate-900">Status do Setup</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Variáveis de Ambiente</p>
              <p className="text-xs text-slate-500">VITE_SUPABASE_URL &amp; VITE_SUPABASE_ANON_KEY</p>
            </div>
            <StatusBadge status={envReady ? "ok" : "pending"} />
          </div>
          <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Acesso Admin</p>
              <p className="text-xs text-slate-500">{userEmail ? `Logado como ${userEmail}` : "Faça login com um usuário admin"}</p>
            </div>
            <StatusBadge status={isAdmin ? "ok" : "pending"} />
          </div>
          <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Google Auth</p>
              <p className="text-xs text-slate-500">Confirme se o provedor está habilitado</p>
            </div>
            <StatusBadge status="pending" label="Verificar manualmente" />
          </div>
        </div>
      </section>

      <section className="mt-8 grid gap-6 md:grid-cols-3">
        <SetupCard
          icon={ShieldCheck}
          title="Configurar Google Auth"
          description="Habilite o provedor Google no Supabase e cadastre as Redirect URLs do projeto."
          primaryAction={{
            label: "Abrir Providers",
            href: providersUrl,
            ariaLabel: "Abrir configuração de providers do Supabase",
          }}
        >
          <div className="space-y-2">
            <p>Adicione as URLs abaixo em <span className="font-semibold">Redirect URLs</span>:</p>
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3">
              <ul className="space-y-1 font-mono text-xs text-slate-700">
                {suggestedRedirects.map(url => (
                  <li key={url}>{url}</li>
                ))}
              </ul>
            </div>
          </div>
        </SetupCard>

        <SetupCard
          icon={Database}
          title="Criar Admin (SQL Editor)"
          description="Execute o SQL abaixo trocando pelo e-mail que deve receber a role de admin."
          primaryAction={{
            label: "Abrir SQL Editor",
            href: sqlUrl,
            ariaLabel: "Abrir SQL Editor do Supabase",
          }}
        >
          <div className="space-y-2">
            <textarea
              readOnly
              value={seedAdminSql}
              className="h-40 w-full resize-none rounded-xl border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-700"
            />
            <p className="text-xs text-slate-500">Dica: faça login com esse e-mail primeiro para que ele exista em auth.users.</p>
          </div>
        </SetupCard>

        <SetupCard
          icon={Users}
          title="Gerenciar Usuários"
          description="Convide, bloqueie ou remova usuários diretamente no Supabase."
          primaryAction={{
            label: "Abrir Users",
            href: usersUrl,
            ariaLabel: "Abrir gerenciamento de usuários do Supabase",
          }}
        >
          <p>Use esta seção para convidar novos membros, atualizar permissões ou remover acessos antigos.</p>
        </SetupCard>
      </section>
    </AdminLayout>
  );
}

export default AdminSetupPage;
